/**
 * A POSTER FRAME FROM A VIDEO, TAKEN IN THE BROWSER (design §5).
 *
 * The server never decodes a video; it re-encodes an image. So the browser
 * draws the frame at about one second onto a canvas and sends it as JPEG
 * beside the file, and the server's preview is made from that. Anything the
 * browser cannot decode (a codec it lacks, a file that is not really a video,
 * a test runtime with no media stack) resolves to null and the upload goes
 * ahead without a poster — never an error to the person uploading.
 */
export function grabPosterFrame(file: File, atSeconds = 1, timeoutMs = 8000): Promise<Blob | null> {
  if (
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let url: string;
    try {
      url = URL.createObjectURL(file);
    } catch {
      resolve(null);
      return;
    }

    const video = document.createElement('video');
    let settled = false;
    const finish = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      try {
        video.load();
      } catch {
        // Nothing to release.
      }
      URL.revokeObjectURL(url);
      resolve(blob);
    };
    const timer = setTimeout(() => {
      finish(null);
    }, timeoutMs);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => {
      finish(null);
    };
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      // A clip shorter than two seconds gives its middle frame.
      video.currentTime = duration > 0 ? Math.min(atSeconds, duration / 2) : 0;
    };
    video.onseeked = () => {
      try {
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (width === 0 || height === 0) {
          finish(null);
          return;
        }
        const scale = Math.min(1, 1600 / width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const context = canvas.getContext('2d');
        if (context === null) {
          finish(null);
          return;
        }
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            finish(blob);
          },
          'image/jpeg',
          0.85,
        );
      } catch {
        finish(null);
      }
    };
    video.src = url;
  });
}
